import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TaskDialogState {
  isOpen: boolean;
  entryTaskId: string | null;
  activeTaskId: string | null;
  sidebarOpen: boolean;
  activityPanelOpen: boolean;
  navigationStack: string[];
}

interface TaskDialogActions {
  openTask: (id: string) => void;
  setActiveTaskId: (id: string) => void;
  pushTaskId: (id: string) => void;
  popTaskId: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleActivityPanel: () => void;
  reset: () => void;
}

type TaskDialogStore = TaskDialogState & TaskDialogActions;

const MAX_STACK_SIZE = 10;

export const useTaskDialogStore = create<TaskDialogStore>()(
  persist(
    (set, get) => ({
      isOpen: false,
      entryTaskId: null,
      activeTaskId: null,
      sidebarOpen: true,
      activityPanelOpen: false,
      navigationStack: [],

      openTask: (id) =>
        set({ isOpen: true, entryTaskId: id, activeTaskId: id, navigationStack: [] }),

      setActiveTaskId: (id) => set({ activeTaskId: id, navigationStack: [] }),

      pushTaskId: (id) => {
        const { activeTaskId, navigationStack } = get();
        const stack = activeTaskId
          ? [...navigationStack, activeTaskId].slice(-MAX_STACK_SIZE)
          : navigationStack;
        set({ activeTaskId: id, navigationStack: stack });
      },

      popTaskId: () => {
        const { navigationStack } = get();
        if (navigationStack.length === 0) return;
        const previous = navigationStack[navigationStack.length - 1];
        set({
          activeTaskId: previous,
          navigationStack: navigationStack.slice(0, -1),
        });
      },

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleActivityPanel: () => set((state) => ({ activityPanelOpen: !state.activityPanelOpen })),

      reset: () =>
        set({ isOpen: false, entryTaskId: null, activeTaskId: null, navigationStack: [] }),
    }),
    {
      name: 'task-dialog-storage',
      // Only persist the sidebar preference — navigation state is session-only
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        activityPanelOpen: state.activityPanelOpen,
      }),
    },
  ),
);
